import {
  HttpContextToken,
  HttpErrorResponse,
  HttpInterceptorFn,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, switchMap, throwError } from 'rxjs';
import { AUTH_CONFIG } from '@auth/config.token';
import { CookiesService } from '../../services/cookies';
import { SessionService } from '../auth/session.service';
import { TokenRefreshService } from '../auth/token-refresh.service';

/** Opt a request out of the 401-refresh-and-replay behaviour. */
export const SKIP_AUTH_REFRESH = new HttpContextToken<boolean>(() => false);

/**
 * Endpoints where a 401 means "bad credentials" or "bad OTP", never "expired
 * session". Refreshing on these would swallow the real error and bounce the
 * user to login instead of showing them what went wrong.
 */
const REFRESH_EXEMPT_PATHS = [
  '/api/auth/login',
  '/api/auth/logout',
  '/api/auth/refresh-token',
  '/api/auth/send-otp',
  '/api/auth/validate-otp',
  '/api/auth/forgot-password',
  '/api/auth/reset-password',
  '/api/auth/confirm-email',
];

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const cookiesService = inject(CookiesService);
  const authConfig = inject(AUTH_CONFIG);
  const refresher = inject(TokenRefreshService);
  const session = inject(SessionService);

  const isBaseApi = req.url.includes(authConfig.baseApi);
  const isSsoApi =
    !!authConfig.ssoBaseApi && req.url.includes(authConfig.ssoBaseApi);

  // Non-API traffic (assets, third parties) passes through untouched.
  if (!isBaseApi && !isSsoApi) {
    return next(req);
  }

  const token = cookiesService.get('token');

  const headers: Record<string, string> = {
    'X-Client-App': 'securepay-web',
    'X-Platform': 'web',
    'X-Developed-by': 'Oluwaseun Adesina',
    'X-Request-Id':
      crypto?.randomUUID?.() ??
      Math.random().toString(36).substring(2) + Date.now(),
  };

  // Only force a JSON Accept header for normal API calls. Binary downloads
  // (responseType 'blob'/'arraybuffer') must keep the browser's default
  // Accept (*/*) — forcing application/json here made the backend return a
  // differently-formatted file than the same request sent via curl/Swagger.
  if (req.responseType === 'json') {
    headers['Accept'] = 'application/json';
  }

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const authorized = req.clone({ setHeaders: headers });

  const lowerUrl = req.url.toLowerCase();
  const exempt =
    req.context.get(SKIP_AUTH_REFRESH) ||
    refresher.isRefreshUrl(req.url) ||
    // The refresh endpoint lives on baseApi and mints a baseApi token; it is
    // unverified whether the admin SSO service accepts it, so SSO 401s
    // propagate as they did before.
    !isBaseApi ||
    REFRESH_EXEMPT_PATHS.some((path) => lowerUrl.includes(path));

  if (exempt) {
    return next(authorized);
  }

  return next(authorized).pipe(
    catchError((error: unknown) => {
      if (!(error instanceof HttpErrorResponse) || error.status !== 401) {
        return throwError(() => error);
      }

      // The session is already over — don't start a refresh that can only fail.
      if (session.status() === 'terminated') {
        return throwError(() => error);
      }

      // Fast path: another request already rotated the token while this one was
      // in flight. Replay with what's in storage now instead of refreshing
      // again — this is what keeps a burst of parallel dashboard calls to a
      // single refresh.
      const current = refresher.currentAccessToken();
      if (current && current !== token) {
        return next(
          authorized.clone({
            setHeaders: { Authorization: `Bearer ${current}` },
          }),
        );
      }

      return refresher.refresh().pipe(
        switchMap((fresh) =>
          // next() runs the chain *downstream* of this interceptor, so a second
          // 401 here propagates to the caller. Exactly one retry per request,
          // guaranteed structurally rather than by a counter.
          next(
            authorized.clone({
              setHeaders: { Authorization: `Bearer ${fresh}` },
            }),
          ),
        ),
        catchError((refreshError: unknown) => {
          // Idempotent: N failing requests produce one logout.
          session.forceLogout('refresh-failed');
          return throwError(() => refreshError);
        }),
      );
    }),
  );
};
